import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import logo from '../../../assets/images/SVG/eduoneLogo.svg';
import ContactAdminModal from '../../common/ContactAdminModal/ContactAdminModal';
import authService from '../../../infrastructure/services/authService';
import AccountSelection from './AccountSelection';

const ResetEmail = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [showAccountSelection, setShowAccountSelection] = useState(false);

    // Simple email format check to show the checkmark on the right of the input
    const isValidEmail = (val) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    };

    const handleVerify = async (e, selectedUserId = null) => {
        if (e) e.preventDefault();
        const trimmedEmail = email.trim();

        if (!trimmedEmail) {
            setError('Email address is required');
            return;
        }

        if (!isValidEmail(trimmedEmail)) {
            setError('Please enter a valid email address');
            return;
        }

        setError('');
        setLoading(true);
        try {
            const result = await authService.forgotPasswordInitiate(trimmedEmail, selectedUserId);

            if (result?.content?.requiresAccountSelection && result?.content?.accounts) {
                setAvailableAccounts(result.content.accounts);
                setShowAccountSelection(true);
                return;
            }

            // Store target in session/localStorage to display on OTP screen and go to next step
            sessionStorage.setItem('resetTarget', trimmedEmail);
            sessionStorage.setItem('resetType', 'email');
            if (selectedUserId !== null) {
                sessionStorage.setItem('resetUserId', selectedUserId);
            } else {
                sessionStorage.removeItem('resetUserId');
            }
            navigate('/forgot-password/otp');
        } catch (err) {
            setError(err?.message || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        sessionStorage.removeItem('resetTarget');
        sessionStorage.removeItem('resetType');
        sessionStorage.removeItem('resetUserId');
        sessionStorage.removeItem('resetOtp');
        navigate('/', { replace: true });
    };

    const handleAccountSelect = (account) => {
        const userId = account.userId || account.id;
        handleVerify(null, userId);
    };

    const handleAccountSelectionBack = () => {
        setShowAccountSelection(false);
        setAvailableAccounts([]);
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '480px',
                my: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                fontFamily: "'Poppins', sans-serif"
            }}
        >
            {/* Logo */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: { xs: 4, md: 5 }
                }}
            >
                <Box
                    component="img"
                    src={logo}
                    alt="EduOne Logo"
                    sx={{
                        height: '45px',
                        width: 'auto'
                    }}
                />
            </Box>

            {/* Header Text */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: { xs: '26px', sm: '28px' },
                        fontWeight: 700,
                        color: '#231F20',
                        mb: 1.5,
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Reset Your Password
                </Typography>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#9B9B9B',
                        lineHeight: 1.6,
                        maxWidth: '400px',
                        mx: 'auto',
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Enter your registered email, and we'll send you a onetime password (OTP) to reset your password.
                </Typography>
            </Box>

            {/* Form */}
            <Box
                component="form"
                onSubmit={handleVerify}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    width: '100%'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1.5px solid',
                            borderColor: error ? '#e74c3c' : '#cbd5e1',
                            borderRadius: '16px',
                            backgroundColor: '#ffffff',
                            transition: 'all 0.25s ease',
                            '&:focus-within': {
                                borderColor: '#ff502d',
                                boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                            }
                        }}
                    >
                        {/* Icon */}
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                        </Box>
                        {/* Divider */}
                        <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                        {/* Input */}
                        <Box
                            component="input"
                            type="text"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (error) setError('');
                            }}
                            sx={{
                                flex: 1,
                                padding: '16px',
                                border: 'none',
                                background: 'transparent',
                                fontSize: '15px',
                                color: '#334155',
                                outline: 'none',
                                fontFamily: "'Poppins', sans-serif",
                                width: '100%'
                            }}
                        />
                        {/* Valid check icon */}
                        {isValidEmail(email) && (
                            <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="10" fill="#2ecc71" />
                                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Box>
                        )}
                    </Box>
                    {error && (
                        <Typography
                            component="span"
                            sx={{
                                color: '#e74c3c',
                                fontSize: '14px',
                                mt: '4px',
                                fontFamily: "'Poppins', sans-serif"
                            }}
                        >
                            {error}
                        </Typography>
                    )}
                </Box>

                {/* Forgot Switcher Link */}
                <Box>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            color: '#475569',
                            fontFamily: "'Poppins', sans-serif"
                        }}
                    >
                        Forgot Email ?{' '}
                        <Box
                            component="span"
                            onClick={() => navigate('/forgot-password/mobile')}
                            sx={{
                                color: '#ff502d',
                                fontWeight: 500,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                transition: 'color 0.2s',
                                '&:hover': {
                                    color: '#e04324'
                                }
                            }}
                        >
                            Use Mobile number
                        </Box>
                    </Typography>
                </Box>

                {/* Verify Button */}
                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !email.trim()}
                    sx={{
                        width: '100%',
                        background: '#ff502d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '16px',
                        py: 2,
                        px: 3,
                        fontSize: '16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textTransform: 'none',
                        boxShadow: '0 8px 20px -6px rgba(255, 80, 45, 0.4)',
                        fontFamily: "'Poppins', sans-serif",
                        transition: 'all 0.25s ease',
                        '&:hover:not(:disabled)': {
                            background: '#e04324',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 24px -6px rgba(255, 80, 45, 0.5)'
                        },
                        '&:disabled': {
                            background: '#ff502d',
                            opacity: 0.6,
                            color: '#fff',
                            boxShadow: 'none',
                            cursor: 'not-allowed'
                        }
                    }}
                >
                    {loading ? 'Sending OTP...' : 'Verify'}
                </Button>

                {/* Back to Login Link */}
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography
                        onClick={handleBackToLogin}
                        sx={{
                            fontSize: '14px',
                            color: '#94a3b8',
                            fontFamily: "'Poppins', sans-serif",
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            textDecoration: 'underline',
                            '&:hover': {
                                color: '#ff502d'
                            }
                        }}
                    >
                        Back to login
                    </Typography>
                </Box>
            </Box>

            <AccountSelection
                open={showAccountSelection}
                accounts={availableAccounts}
                onSelect={handleAccountSelect}
                onClose={handleAccountSelectionBack}
                loading={loading}
            />

            {/* Help Support Footer */}
            <Box sx={{ textAlign: 'center', mt: 5, mb: 1 }}>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#475569',
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Need help to reset password ?{' '}
                    <Box
                        component="span"
                        onClick={() => setIsContactOpen(true)}
                        sx={{
                            color: '#ff502d',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#e04324'
                            }
                        }}
                    >
                        Contact us
                    </Box>
                </Typography>
            </Box>

            {/* Reusable ContactAdminModal */}
            <ContactAdminModal
                isOpen={isContactOpen}
                onClose={() => setIsContactOpen(false)}
            />
        </Box>
    );
};

export default ResetEmail;
